import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { DrawerSteps } from 'components/Reservation/types/types';
import { CarOptionType } from 'store/slices/cars/types';
import moment from 'moment';
import type { User } from 'types/users';
import type { CarOptions, InitialState, RentTime } from './types';

const initialState: InitialState = {
  rentalDrawerErrorFields: new Map(),
  rentDate: null,
  rentalDrawerStep: DrawerSteps.CAR_DETAILS,
  rentTime: {
    start: moment({ hour: 10, minute: 0, second: 0 }).format('HH:mm'),
    end: moment({ hour: 10, minute: 0, second: 0 }).format('hh:mm'),
  },
  checkedOptions: Object.keys(CarOptionType).reduce(
    (prev, current) => ({
      ...prev,
      [current]: false,
    }),
    {},
  ) as CarOptions,
  totalPrice: 0,
  priceForDistance: 0,
  priceForDate: 0,
  startLocation: null,
  endLocation: null,
  notesFromCustomer: '',
  deliveryKms: 0,
  isUserUpdated: false,
  isTermsAndConditionsAccepted: false,
  isOrderCreationProcess: false,
  isSubscriptionActivated: false,
  isSecondaryDriverChecked: false,
  isAccountCreationStepPending: false,
};

export const rentDetailsSlice = createSlice({
  name: 'rentDetails',
  initialState,
  reducers: {
    setIsAccountCreationStepPending: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      return {
        ...state,
        isAccountCreationStepPending: action.payload,
      };
    },
    setIsSecondaryDriverChecked: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isSecondaryDriverChecked: action.payload,
      };
    },
    resetStore: (state) => {
      return {
        ...state,
        ...initialState,
        rentalDrawerStep: state.rentalDrawerStep,
      };
    },
    setIsSubscriptionActivated: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isSubscriptionActivated: action.payload,
      };
    },
    setIsOrderCreationInProcess: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isOrderCreationProcess: action.payload,
      };
    },
    setTermsAndConditionsAcceptedStatus: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      return {
        ...state,
        isTermsAndConditionsAccepted: action.payload,
      };
    },
    setIsUserUpdated: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isUserUpdated: action.payload,
      };
    },
    removeRentalDrawerErrorFields: (
      state,
      action: PayloadAction<keyof User | `secondaryDriver.${keyof User}`>,
    ) => {
      state.rentalDrawerErrorFields.delete(action.payload);
    },
    setRentalDrawerErrorFields: (state, action: PayloadAction<any>) => {
      action.payload.forEach((item: any) => {
        state.rentalDrawerErrorFields.set(item.path, item.message);
      });
    },
    resetRentalDrawerErrorFields: (state) => {
      return {
        ...state,
        rentalDrawerErrorFields: new Map(),
      };
    },
    setDeliveryKms: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        deliveryKms: action.payload,
      };
    },
    setNotesFromCustomer: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        notesFromCustomer: action.payload,
      };
    },
    resetState: (state, action: PayloadAction<Partial<InitialState>>) => {
      return {
        ...initialState,
        ...action.payload,
      };
    },
    setDrawerStep: (state, action: PayloadAction<DrawerSteps>) => {
      return {
        ...state,
        rentalDrawerStep: action.payload,
      };
    },
    setRentDate: (state, action: PayloadAction<(Date | null)[]>) => {
      return {
        ...state,
        rentDate: action.payload,
      };
    },
    setRentTime: (state, action: PayloadAction<RentTime>) => {
      return {
        ...state,
        rentTime: action.payload,
      };
    },
    setCheckedOptions: (state, action: PayloadAction<CarOptions>) => {
      return {
        ...state,
        checkedOptions: action.payload,
      };
    },
    setTotalPrice: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        totalPrice: action.payload,
      };
    },
    setPriceForDistance: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        priceForDistance: action.payload,
      };
    },
    setPriceForDate: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        priceForDate: action.payload,
      };
    },
    setStartLocation: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        startLocation: action.payload,
      };
    },
    setEndLocation: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        endLocation: action.payload,
      };
    },
  },
});
